import { Card, Flex, Skeleton, Stack, Text } from '@mantine/core'
import { formatResponseTime } from '@/lib/utilities'

interface VisitOptionCardProps {
    title: string
    /** Optional secondary line under the title (e.g. "With a local clinician"). */
    subtitle?: string
    /** When true, the literal `title` is used as-is; otherwise " Visit" is appended. */
    titleVerbatim?: boolean
    icon: React.ReactNode
    price?: string
    insuranceText?: string
    secondaryPrice?: string
    footnote?: string
    skuId: string
    optionType: string
    userHomeTimezone?: string | null
    availability?: string | null
    availabilityLoading?: boolean
    theme?: 'default' | 'tight'
    /**
     * When true, sizing is responsive (full-width on mobile, 250 on desktop)
     * and height grows with content — regardless of theme. Use when the card
     * is a standalone primary CTA in a vertical layout where the default
     * fixed 250×250 would look narrower than its full-width siblings.
     */
    responsive?: boolean
    onClick?: () => void
}

export function VisitOptionCard({
    title,
    subtitle,
    titleVerbatim = false,
    icon,
    price,
    insuranceText,
    secondaryPrice,
    footnote,
    skuId,
    optionType,
    userHomeTimezone,
    availability,
    availabilityLoading,
    theme = 'default',
    responsive = false,
    onClick,
}: VisitOptionCardProps) {
    const fluidSizing = responsive || theme === 'tight'
    return (
        <Card
            withBorder
            radius="var(--border-16, 16px)"
            bg="var(--gm-background-bg-card)"
            bd={'1px solid var(--Foreground-fg-outline-primary, #D1D0CF)'}
            shadow="0px 1px 1px 0px rgba(64, 63, 62, 0.05), 0px 2px 7px 0px rgba(64, 63, 62, 0.05)"
            h={fluidSizing ? 'auto' : 250}
            w={fluidSizing ? { base: '100%', md: 250 } : 250}
            p={24}
            onClick={onClick}
            style={onClick ? { cursor: 'pointer' } : undefined}
            data-testid={`visit-option-card-${optionType}`}
        >
            <Stack
                h={'100%'}
                justify={theme === 'tight' ? 'flex-start' : 'space-between'}
                gap={theme === 'tight' ? 12 : 0}
            >
                <Stack gap={theme === 'tight' ? 16 : undefined}>
                    {renderHeader()}
                    {theme === 'tight' ? (
                        <Flex align="baseline" gap={8} wrap="wrap">
                            {renderPrice()}
                            {renderInsuranceText()}
                        </Flex>
                    ) : (
                        <Stack gap={8}>
                            {renderPrice()}
                            {renderInsuranceText()}
                        </Stack>
                    )}
                    {footnote && (
                        <Text
                            c={'var(--gm-foreground-fg-secondary, #4F4E4D)'}
                            fz={theme === 'tight' ? 12 : 14}
                            fw={theme === 'tight' ? 'var(--weight-text, 450)' : 500}
                            lh={'150%'}
                            mt={4}
                            data-testid={`visit-option-footnote-${optionType}`}
                        >
                            {footnote}
                        </Text>
                    )}
                </Stack>
                {renderAvailability()}
            </Stack>
        </Card>
    )

    function renderHeader() {
        return (
            <Flex justify="space-between" align="center" w={'100%'} data-testid={`visit-option-header-${optionType}`}>
                <Stack gap={2} justify="center" w={'100%'}>
                    <Text
                        c={'var(--Foreground-fg-primary, #1F1E1D)'}
                        fz={theme === 'tight' ? 20 : 18}
                        fw={theme === 'tight' ? 'var(--weight-text, 450)' : 'var(--weight-text, 500)'}
                        lh={'130%'}
                        data-testid={`visit-option-title-${optionType}`}
                    >
                        {titleVerbatim ? title : `${title} Visit`}
                    </Text>
                    {subtitle ? (
                        <Text
                            c={'var(--gm-foreground-fg-secondary, #4F4E4D)'}
                            fz={14}
                            fw={'var(--weight-text, 500)'}
                            lh={'130%'}
                            data-testid={`visit-option-subtitle-${optionType}`}
                        >
                            {subtitle}
                        </Text>
                    ) : null}
                </Stack>

                {theme !== 'tight' ? (
                    <Flex
                        bg={'var(--Background-bg-tonal, rgba(43, 42, 42, 0.05))'}
                        gap={'10px'}
                        p={'var(--spacing-8, 8px)'}
                        style={{
                            justifyContent: 'center',
                            alignItems: 'center',
                            borderRadius: 'var(--border-round, 999px)',
                        }}
                        data-testid={`visit-option-icon-${optionType}`}
                    >
                        {icon}
                    </Flex>
                ) : null}
            </Flex>
        )
    }

    function renderPrice() {
        return (
            <Text
                c={'var(--Foreground-fg-primary, #1F1E1D)'}
                fw={theme === 'tight' ? 'var(--weight-text-bold, 600)' : 'var(--gm-weight-display-small, 450)'}
                lh={'120%'}
                lts={'-0.36px'}
                fz={theme === 'tight' ? 20 : 30}
                data-testid={`visit-option-price-${optionType}`}
            >
                {price}
            </Text>
        )
    }

    function renderInsuranceText() {
        return (
            <Text
                c={'var(--gm-foreground-fg-secondary, #4F4E4D)'}
                fz={theme === 'tight' ? 12 : 14}
                fw={theme === 'tight' ? 'var(--weight-text, 450)' : 500}
                lh={'150%'}
                data-testid={`visit-option-price-details-${optionType}`}
            >
                {insuranceText}
                {secondaryPrice
                    ? theme === 'tight'
                        ? `${insuranceText ? '\u00A0' : ''}${secondaryPrice}`
                        : ', ' + secondaryPrice
                    : ''}
            </Text>
        )
    }

    function isValidDateString(dateString: string | null | undefined) {
        if (!dateString) return false
        const date = new Date(dateString)
        return !isNaN(date.getTime())
    }

    function renderAvailability() {
        if (availabilityLoading) {
            return (
                <Skeleton
                    height={theme === 'tight' ? 16 : 28}
                    width={183}
                    radius="var(--border-round, 999px)"
                    data-testid={`visit-option-availability-loading-${optionType}`}
                />
            )
        }

        let availabilityText = null
        if ((optionType === 'video' || optionType === 'econsult') && availability) {
            availabilityText = `Appointments ${availability}`
        } else if (isValidDateString(availability)) {
            availabilityText = (
                <>
                    Response by&nbsp;
                    {userHomeTimezone
                        ? formatResponseTime(availability!, false, true, userHomeTimezone)
                        : formatResponseTime(availability!, false, true)}
                </>
            )
        }
        if (!availabilityText) return null

        return (
            <Flex
                py={theme === 'tight' ? 0 : availabilityText.toString().length >= 22 ? 4 : 6}
                px={theme === 'tight' ? 0 : availabilityText.toString().length >= 22 ? 7 : 8}
                justify={'center'}
                align={'center'}
                gap={'var(--spacing-2, 2px)'}
                bg={
                    theme === 'tight'
                        ? 'transparent'
                        : 'var(--Background-bg-positive-container, rgba(22, 179, 100, 0.15))'
                }
                style={{
                    borderRadius: theme === 'tight' ? 0 : 'var(--border-round, 999px)',
                    alignSelf: 'start',
                    padding: theme === 'tight' ? 0 : undefined,
                }}
                data-testid={`visit-option-availability-container-${optionType}`}
            >
                <Text
                    c={'var(--Foreground-fg-positive, #087442)'}
                    fz={theme === 'tight' ? 12 : 14}
                    fw={'var(--weight-label, 550)'}
                    lh={theme === 'tight' ? '16px' : '20px'}
                    data-testid={`visit-option-availability-text-${optionType}`}
                >
                    {availabilityText}
                </Text>
            </Flex>
        )
    }
}
